/**
 * useCourseGeneration Hook
 *
 * Manages background course generation with SSE streaming.
 * Handles starting new generation jobs, tracking progress,
 * and showing notifications on completion.
 *
 * @author Tutara Team
 * @since January 2025
 */

import { useCallback, useEffect, useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import authApi from '@/services/auth/api';
import courseApi, { type CourseGenerationJob as BackendJob } from '@/services/course/api';
import type { ClarifyingQuestion } from '@/services/course/types';
import {
  useCourseGenerationStore,
  type CourseGenerationFormData,
  type CourseGenerationJob,
  type CourseGenerationErrorCode,
} from '@/stores/course-generation-store';
import {
  requestNotificationPermission,
  showCourseReadyNotification,
  showCourseFailedNotification,
  shouldPromptForPermission,
} from '@/utils/browser-notifications';
import { toast } from '@/utils/toast';
import type { GeneratedCourse, CourseGenerationProgress } from '@/services/course/types';
import { useAuthStore } from '@/stores/auth-store';
import { API_BASE_URL } from '@/config/env';

// =============================================================================
// Constants
// =============================================================================

const SSE_INACTIVITY_WARNING_MS = 30000; // 30 seconds
const SSE_INACTIVITY_TIMEOUT_MS = 60000; // 1 minute (reduced from 2 minutes for faster error detection)

/** Max user-initiated retry attempts per generation. Past this we stop offering
 * a Retry button — at that point the user should edit inputs or check setup. */
const MAX_RETRY_ATTEMPTS = 3;

// =============================================================================
// Types
// =============================================================================

interface ActiveConnection {
  eventSource: EventSource;
  timeoutChecker: NodeJS.Timeout;
  lastEventTime: number;
  lastProgress?: number;
}

// =============================================================================
// Module-level connection registry (shared across all hook instances)
// This prevents SSE connections from being killed when individual components
// unmount during page navigation. Connections are only cleaned up when a job
// completes, fails, is cancelled, or times out.
// =============================================================================

const activeConnections = new Map<string, ActiveConnection>();

// =============================================================================
// Clarifications cache (module-level so it survives modal remounts)
// Key: `${topic.trim().toLowerCase()}|${sortedKnowledge.join(',')}|${difficulty}`
// Stores resolved questions (may be empty array to cache "no quiz needed").
// Also tracks in-flight requests to dedupe overlapping calls.
// =============================================================================

const clarificationsCache = new Map<string, ClarifyingQuestion[]>();
const clarificationsInflight = new Map<string, Promise<ClarifyingQuestion[]>>();

function clarificationsCacheKey(
  topic: string,
  knowledge: string[],
  difficulty: string
): string {
  const normalizedTopic = topic.trim().toLowerCase();
  const sortedKnowledge = [...knowledge].map((k) => k.trim()).filter(Boolean).sort();
  return `${normalizedTopic}|${sortedKnowledge.join(',')}|${difficulty}`;
}

// =============================================================================
// Hook
// =============================================================================

export function useCourseGeneration() {
  const queryClient = useQueryClient();

  // Auth state for restoration
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthReady = useAuthStore((state) => state.isAuthReady);

  // Track if we've already attempted restoration this session
  const [hasRestoredFromBackend, setHasRestoredFromBackend] = useState(false);

  // Store state and actions - use useShallow for array to prevent infinite loops
  const jobs = useCourseGenerationStore(useShallow((state) => state.jobs));
  const addJob = useCourseGenerationStore((state) => state.addJob);
  const updateJobProgress = useCourseGenerationStore((state) => state.updateJobProgress);
  const completeJob = useCourseGenerationStore((state) => state.completeJob);
  const failJob = useCourseGenerationStore((state) => state.failJob);
  const cancelJob = useCourseGenerationStore((state) => state.cancelJob);
  const removeJob = useCourseGenerationStore((state) => state.removeJob);
  const restoreJob = useCourseGenerationStore((state) => state.restoreJob);

  // Compute derived state with useMemo to prevent infinite loops
  const activeJobs = useMemo(
    () =>
      jobs.filter(
        (job) =>
          job.status === 'queued' ||
          job.status === 'connecting' ||
          job.status === 'generating'
      ),
    [jobs]
  );

  const hasActiveJobs = activeJobs.length > 0;
  const activeJobsCount = activeJobs.length;

  /**
   * Clean up a specific connection from the shared registry
   */
  const cleanupConnection = useCallback((jobId: string) => {
    const connection = activeConnections.get(jobId);
    if (connection) {
      connection.eventSource.close();
      clearInterval(connection.timeoutChecker);
      activeConnections.delete(jobId);
    }
  }, []);

  /**
   * Start course generation for a job.
   *
   * 3-call sequence (see course-clarifications-plan §7):
   *   1. POST /api/core/courses/generate/prepare  → { jobId }
   *   2. POST /api/v1/auth/sse-token              → { sseToken }
   *   3. GET  /api/core/courses/generate/stream?jobId=...&sseToken=...  (SSE)
   *
   * The backend-issued jobId from step 1 replaces the locally-generated
   * job id used for store bookkeeping.
   */
  const startGeneration = useCallback(
    async (jobId: string, job: CourseGenerationJob) => {
      // Skip if a connection already exists (another hook instance started it)
      if (activeConnections.has(jobId)) {
        return;
      }

      try {
        // Update status to connecting
        updateJobProgress(jobId, {
          status: 'connecting',
          message: 'Preparing generation...',
          progress: 0,
        });

        // Parse textarea lines into arrays — the old query-param endpoint
        // accepted raw comma-separated strings and split server-side; the
        // new JSON endpoint expects arrays.
        const toList = (raw: string): string[] =>
          raw
            .split(/[\n,]/)
            .map((item) => item.trim())
            .filter(Boolean);

        // Step 1: prepare the generation job
        const prepareResponse = await courseApi.prepareGeneration({
          courseTopic: job.courseTopic,
          existingKnowledge: toList(job.experience),
          hobbiesInterests: toList(job.hobbies),
          preferredLanguage: job.language,
          difficultyLevel: job.difficulty,
          clarifications: job.clarifications,
          clarificationsFreeform: job.clarificationsFreeform,
        });

        const backendJobId = prepareResponse?.jobId;
        if (!backendJobId) {
          throw new Error('Failed to prepare course generation job');
        }

        // The backend owns the real jobId from here on. Re-key the local job
        // so SSE events + connection-registry lookups match.
        if (backendJobId !== jobId) {
          useCourseGenerationStore.setState((state) => ({
            jobs: state.jobs.map((j) =>
              j.id === jobId ? { ...j, id: backendJobId } : j
            ),
          }));
          jobId = backendJobId;
        }

        updateJobProgress(jobId, {
          status: 'connecting',
          message: 'Authenticating...',
          progress: 3,
        });

        // Step 2: mint a single-use SSE token
        const sseTokenResponse = await authApi.requestSseToken({
          purpose: 'course-generation',
          metadata: job.courseTopic,
        });

        if (!sseTokenResponse.success || !sseTokenResponse.token) {
          throw new Error('Failed to authenticate for course generation');
        }

        updateJobProgress(jobId, {
          status: 'connecting',
          message: 'Connecting to AI...',
          progress: 5,
        });

        // Step 3: open the SSE stream
        const params = new URLSearchParams({
          jobId,
          sseToken: sseTokenResponse.token,
        });

        const timestamp = Date.now();
        const eventSource = new EventSource(
          `${API_BASE_URL}/api/core/courses/generate/stream?${params}&_t=${timestamp}`
        );

        // Track last event time for timeout detection
        let lastEventTime = Date.now();

        // Start timeout checker
        const timeoutChecker = setInterval(() => {
          const timeSinceLastEvent = Date.now() - lastEventTime;

          if (timeSinceLastEvent > SSE_INACTIVITY_TIMEOUT_MS) {
            console.warn(`[CourseGeneration] SSE timeout for job ${jobId}`);
            cleanupConnection(jobId);
            failJob(jobId, 'Connection timeout. The AI service took too long to respond.');
            toast.error('Course generation timed out');
            showCourseFailedNotification(job.courseTopic, 'Connection timed out');
          } else if (timeSinceLastEvent > SSE_INACTIVITY_WARNING_MS) {
            const conn = activeConnections.get(jobId);
            updateJobProgress(jobId, {
              status: 'generating',
              message: 'Still generating... AI is working on a complex course structure.',
              progress: Math.min(90, conn?.lastProgress || 50),
            });
          }
        }, 10000);

        // Store connection in shared registry
        activeConnections.set(jobId, {
          eventSource,
          timeoutChecker,
          lastEventTime,
        });

        // Progress events
        eventSource.addEventListener('progress', (event) => {
          lastEventTime = Date.now();
          const connection = activeConnections.get(jobId);
          if (connection) {
            connection.lastEventTime = lastEventTime;
            connection.lastProgress = JSON.parse(event.data).progress;
          }

          try {
            const data = JSON.parse(event.data) as CourseGenerationProgress;
            updateJobProgress(jobId, data);
          } catch {
            console.warn('[CourseGeneration] Failed to parse progress event');
          }
        });

        // Heartbeat events
        eventSource.addEventListener('heartbeat', (event) => {
          lastEventTime = Date.now();
          const connection = activeConnections.get(jobId);
          if (connection) {
            connection.lastEventTime = lastEventTime;
          }

          try {
            const data = JSON.parse(event.data) as CourseGenerationProgress;
            updateJobProgress(jobId, data);
          } catch {
            console.warn('[CourseGeneration] Failed to parse heartbeat event');
          }
        });

        // Course completion
        eventSource.addEventListener('course', async (event) => {
          cleanupConnection(jobId);

          try {
            const course = JSON.parse(event.data) as GeneratedCourse;

            // Complete the job in store
            completeJob(jobId, course);

            // Auto-save course
            try {
              await courseApi.saveCourse({
                courseId: course.courseId,
                isGenerated: true,
                courseTitle: course.courseName,
                courseDescription: course.description || `AI-generated course for ${job.courseTopic}`,
                courseDifficulty: course.difficultyLevel || job.difficulty,
                courseDuration: course.estimatedHours,
                originalTopic: job.courseTopic,
              });

              // Invalidate queries to refresh course lists
              queryClient.invalidateQueries({ queryKey: ['my-courses'] });
              queryClient.invalidateQueries({ queryKey: ['learning-items'] });

              toast.success(`Course "${course.courseName}" is ready!`);
            } catch {
              console.warn('[CourseGeneration] Failed to auto-save course');
              toast.warning('Course generated but auto-save failed.');
            }

            // Show browser notification
            showCourseReadyNotification(course.courseName, () => {
              // Could navigate to course here if needed
              window.focus();
            });
          } catch {
            console.error('[CourseGeneration] Failed to parse course data');
            failJob(jobId, 'Failed to process generated course');
            toast.error('Failed to process generated course');
          }
        });

        // Backend error event - sent when course generation fails on the server
        // Uses 'generation-error' to avoid conflict with EventSource's built-in 'error' event
        eventSource.addEventListener('generation-error', (event) => {
          cleanupConnection(jobId);

          try {
            const errorData = JSON.parse(event.data) as { message: string };
            const errorMsg = errorData.message || 'Course generation failed';

            console.error('[CourseGeneration] Backend error:', errorMsg);
            failJob(jobId, errorMsg);
            toast.error(errorMsg);
            showCourseFailedNotification(job.courseTopic, errorMsg);
          } catch {
            // Fallback if parsing fails
            const errorMsg = 'Course generation failed unexpectedly';
            failJob(jobId, errorMsg);
            toast.error(errorMsg);
            showCourseFailedNotification(job.courseTopic, errorMsg);
          }
        });

        // Connection-level error handling (network issues, server disconnect, etc.)
        // The backend /stream endpoint ALSO emits a named `error` event with
        // a JSON payload ({code, message}) for auth/ownership/payload failures;
        // MessageEvent.data is populated in that case, undefined for raw
        // connection errors.
        eventSource.addEventListener('error', (event: Event) => {
          // Check connection BEFORE cleanup (cleanup deletes it from the map)
          const connection = activeConnections.get(jobId);
          if (!connection) {
            // Already cleaned up (completed, failed, or cancelled)
            return;
          }

          cleanupConnection(jobId);

          // Backend-sent structured error (UNAUTHORIZED, JOB_NOT_FOUND, PAYLOAD_NOT_FOUND)
          const backendData = (event as MessageEvent).data;
          if (typeof backendData === 'string' && backendData.length > 0) {
            try {
              const parsed = JSON.parse(backendData) as { code?: string; message?: string };
              const code = parsed.code ?? 'ERROR';
              let errorMsg: string;
              let errorCode: CourseGenerationErrorCode;
              if (code === 'PAYLOAD_NOT_FOUND' || code === 'JOB_NOT_FOUND') {
                errorMsg = 'Generation session expired. Please try again.';
                errorCode = 'EXPIRED';
              } else if (code === 'UNAUTHORIZED') {
                errorMsg = 'Session expired. Please sign in again.';
                errorCode = 'UNAUTHORIZED';
              } else {
                errorMsg = parsed.message || 'Course generation failed';
                errorCode = 'OTHER';
              }

              failJob(jobId, errorMsg, errorCode);
              toast.error(errorMsg);
              showCourseFailedNotification(job.courseTopic, errorMsg);
              return;
            } catch {
              // Fall through to connection-error handling below.
            }
          }

          const timeSinceLastEvent = Date.now() - lastEventTime;
          let errorMsg: string;
          let errorCode: CourseGenerationErrorCode;

          if (timeSinceLastEvent > SSE_INACTIVITY_TIMEOUT_MS) {
            errorMsg = 'Connection timeout. The AI service took too long to respond.';
            errorCode = 'TIMEOUT';
          } else if (eventSource.readyState === EventSource.CLOSED) {
            errorMsg = 'Connection closed unexpectedly. Please check if the backend is running.';
            errorCode = 'CONNECTION';
          } else {
            errorMsg = 'Failed to generate course. Please try again.';
            errorCode = 'OTHER';
          }

          failJob(jobId, errorMsg, errorCode);
          toast.error(errorMsg);
          showCourseFailedNotification(job.courseTopic, errorMsg);
        });
      } catch (error) {
        cleanupConnection(jobId);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to start course generation';
        failJob(jobId, errorMessage);
        toast.error(errorMessage);
      }
    },
    [updateJobProgress, completeJob, failJob, cleanupConnection, queryClient]
  );

  /**
   * Fetch (or reuse cached) clarifying questions for a (topic, knowledge, difficulty)
   * tuple. Empty arrays are cached too — callers should treat `[]` as "no quiz".
   * Network/API failures are swallowed and also cached as `[]` so the modal
   * never needs to distinguish skip from failure.
   */
  const fetchClarifications = useCallback(
    async (
      topic: string,
      knowledge: string[],
      difficulty: string,
      hobbies: string[] = []
    ): Promise<ClarifyingQuestion[]> => {
      const key = clarificationsCacheKey(topic, knowledge, difficulty);

      const cached = clarificationsCache.get(key);
      if (cached !== undefined) {
        return cached;
      }

      const inflight = clarificationsInflight.get(key);
      if (inflight) {
        return inflight;
      }

      const promise = courseApi
        .getClarifications({
          topic: topic.trim(),
          difficulty,
          existingKnowledge: knowledge.map((k) => k.trim()).filter(Boolean),
          hobbies: hobbies.map((h) => h.trim()).filter(Boolean),
        })
        .then((res) => res?.questions ?? [])
        .catch((err) => {
          console.warn('[CourseGeneration] Clarifications fetch failed:', err);
          return [] as ClarifyingQuestion[];
        })
        .then((questions) => {
          clarificationsCache.set(key, questions);
          clarificationsInflight.delete(key);
          return questions;
        });

      clarificationsInflight.set(key, promise);
      return promise;
    },
    []
  );

  /**
   * Start a new background course generation
   */
  const startBackgroundGeneration = useCallback(
    async (formData: CourseGenerationFormData): Promise<string> => {
      // Request notification permission if not yet asked
      if (shouldPromptForPermission()) {
        requestNotificationPermission();
      }

      // Add job to store
      const jobId = addJob(formData);

      // Get the job from store
      const job = useCourseGenerationStore.getState().getJobById(jobId);
      if (!job) {
        throw new Error('Failed to create generation job');
      }

      // Start generation asynchronously
      startGeneration(jobId, job);

      return jobId;
    },
    [addJob, startGeneration]
  );

  /**
   * Cancel a generation job
   */
  const cancelGenerationJob = useCallback(
    (jobId: string) => {
      cleanupConnection(jobId);
      cancelJob(jobId);
      toast.info('Course generation cancelled');
    },
    [cleanupConnection, cancelJob]
  );

  /**
   * Dismiss/remove a completed or failed job
   */
  const dismissJob = useCallback(
    (jobId: string) => {
      cleanupConnection(jobId);
      removeJob(jobId);
    },
    [cleanupConnection, removeJob]
  );

  /**
   * Retry a failed generation using the form data already stored on the job.
   * Refuses if the failure was UNAUTHORIZED (re-auth needed) or the user has
   * already retried MAX_RETRY_ATTEMPTS times. Tears down the failed job and
   * kicks off a fresh attempt that inherits the cumulative retryCount, so the
   * panel only shows the latest attempt and the cap can't be bypassed by
   * clicking again.
   */
  const retryJob = useCallback(
    async (jobId: string) => {
      const job = useCourseGenerationStore.getState().getJobById(jobId);
      if (!job) return;
      if (job.errorCode === 'UNAUTHORIZED') {
        toast.error('Session expired. Please sign in again to retry.');
        return;
      }
      const nextAttempt = (job.retryCount ?? 0) + 1;
      if (nextAttempt > MAX_RETRY_ATTEMPTS) {
        toast.error(`Already retried ${MAX_RETRY_ATTEMPTS} times. Try editing your inputs.`);
        return;
      }
      if (!job.courseTopic) {
        toast.error('Cannot retry — original generation inputs are missing.');
        return;
      }

      cleanupConnection(jobId);
      removeJob(jobId);

      const newJobId = await startBackgroundGeneration({
        courseTopic: job.courseTopic,
        experience: job.experience,
        hobbies: job.hobbies,
        language: job.language,
        difficulty: job.difficulty,
        clarifications: job.clarifications,
        clarificationsFreeform: job.clarificationsFreeform,
      });
      // Carry the retryCount forward onto the freshly-created job so the cap
      // is enforced across attempts.
      useCourseGenerationStore.setState((state) => ({
        jobs: state.jobs.map((j) =>
          j.id === newJobId ? { ...j, retryCount: nextAttempt } : j
        ),
      }));
    },
    [cleanupConnection, removeJob, startBackgroundGeneration]
  );

  // Resume active jobs that don't have connections in the shared registry.
  // This handles page refresh (connections lost) and any edge cases where
  // a job exists in the store but has no active SSE connection.
  useEffect(() => {
    activeJobs.forEach((job) => {
      if (!activeConnections.has(job.id) && job.status === 'queued') {
        startGeneration(job.id, job);
      }
    });
  }, [activeJobs, startGeneration]);

  /**
   * Convert backend job status to frontend status
   */
  const mapBackendStatus = (
    status: BackendJob['status']
  ): CourseGenerationJob['status'] => {
    const statusMap: Record<BackendJob['status'], CourseGenerationJob['status']> = {
      QUEUED: 'queued',
      CONNECTING: 'connecting',
      GENERATING: 'generating',
      COMPLETED: 'completed',
      FAILED: 'failed',
      CANCELLED: 'cancelled',
    };
    return statusMap[status] || 'queued';
  };

  /**
   * Restore jobs from backend after page refresh
   */
  useEffect(() => {
    // Only restore once per session and when auth is validated
    if (hasRestoredFromBackend || !isAuthReady || !isAuthenticated) {
      return;
    }

    const restoreJobsFromBackend = async () => {
      try {
        const backendJobs = await courseApi.getGenerationJobs(20);

        if (backendJobs && backendJobs.length > 0) {
          backendJobs.forEach((backendJob) => {
            // Check if job already exists in local store
            const existingJob = useCourseGenerationStore.getState().getJobById(backendJob.jobId);

            if (!existingJob) {
              // Restore job to local store
              restoreJob({
                id: backendJob.jobId,
                courseTopic: backendJob.courseTopic,
                experience: backendJob.experience || '',
                hobbies: backendJob.hobbies || '',
                language: backendJob.language || 'English',
                status: mapBackendStatus(backendJob.status),
                progress: backendJob.progress || 0,
                currentStep: backendJob.currentStep || 'Restoring...',
                errorMessage: backendJob.errorMessage,
                courseId: backendJob.courseId,
                createdAt: new Date(backendJob.createdAt).getTime(),
              });
            } else {
              // Update existing job with latest backend state
              const frontendStatus = mapBackendStatus(backendJob.status);

              if (frontendStatus === 'completed' && existingJob.status !== 'completed') {
                completeJob(backendJob.jobId, { courseId: backendJob.courseId });
              } else if (frontendStatus === 'failed' && existingJob.status !== 'failed') {
                failJob(backendJob.jobId, backendJob.errorMessage || 'Generation failed');
              } else if (
                frontendStatus === 'generating' ||
                frontendStatus === 'connecting' ||
                frontendStatus === 'queued'
              ) {
                updateJobProgress(backendJob.jobId, {
                  status: frontendStatus,
                  message: backendJob.currentStep || 'In progress...',
                  progress: backendJob.progress || 0,
                });
              }
            }
          });

          // Reconnect SSE for active jobs that don't have connections
          const store = useCourseGenerationStore.getState();
          const currentActiveJobs = Object.values(store.jobs).filter(
            (job) => job.status === 'queued' || job.status === 'connecting' || job.status === 'generating'
          );

          currentActiveJobs.forEach((job) => {
            if (!activeConnections.has(job.id)) {
              startGeneration(job.id, job);
            }
          });
        }
      } catch (error) {
        console.warn('[CourseGeneration] Failed to restore jobs from backend:', error);
        // Non-critical error - don't show to user, local state will still work
      } finally {
        setHasRestoredFromBackend(true);
      }
    };

    restoreJobsFromBackend();
  }, [
    hasRestoredFromBackend,
    isAuthReady,
    isAuthenticated,
    restoreJob,
    completeJob,
    failJob,
    updateJobProgress,
    startGeneration,
  ]);

  return {
    // State
    jobs,
    activeJobs,
    hasActiveJobs,
    activeJobsCount,

    // Actions
    startBackgroundGeneration,
    cancelGenerationJob,
    dismissJob,
    retryJob,
    fetchClarifications,
  };
}

export default useCourseGeneration;
